/**
 * batch-49 acquisitie-threads (FO-399) - gedeelde validatie-regels voor het
 * vervolg-contactmoment.
 *
 * Eén bron voor acquisitie (`AcquisitieContactmoment`) én CRM
 * (`ClientContactMoment`): dezelfde regels, dezelfde teksten, zowel
 * client-side (inline-invoer valideert vóór verzenden) als server-side (de
 * bestaande create-schema's van beide routes hangen deze refinement erin).
 *
 * Let op: dit is GEEN aparte "vervolg-variant" van het create-schema (zie spec
 * §8.3). De routes valideren gewoon hun volledige eigen schema; dit bestand
 * levert alleen het extra `vervolgOpId`-veld + de conditionele regels die
 * daarbij horen, plus het invoer-schema van het inline formulier.
 */

import { z } from 'zod'
import {
  issuesToFieldErrors,
  type ApiValidationError,
  type ApiValidationIssue,
} from './validation-issues'

/** Inhoud van een vervolg is verplicht en moet iets zeggen (spec §5.2). */
export const CONTACTMOMENT_VERVOLG_INHOUD_MIN = 3

export const VERVOLG_INHOUD_TE_KORT = `Vul minimaal ${CONTACTMOMENT_VERVOLG_INHOUD_MIN} tekens in`
export const VERVOLG_DATUM_VOOR_BOVENLIGGEND =
  'Datum mag niet vóór het bovenliggende contactmoment liggen'
/** Spiegelbeeld bij bewerken: een wortel mag niet ná zijn eerste vervolg schuiven. */
export const VERVOLG_WORTEL_NA_EERSTE_VERVOLG =
  'Datum mag niet ná het eerste vervolg in deze draad liggen'
export const VERVOLG_BOVENLIGGEND_NIET_GEVONDEN =
  'Het bovenliggende contactmoment bestaat niet (meer)'

/** Optioneel draad-veld op een create-schema: id van het moment waarop dit een vervolg is. */
export const vervolgOpIdVeld = z.string().min(1).nullable().optional()

/**
 * Conditionele regel voor een create-schema: is `vervolgOpId` gezet, dan moet
 * de inhoud minimaal `CONTACTMOMENT_VERVOLG_INHOUD_MIN` tekens hebben.
 * `inhoudVeld` is de naam van het inhoud-veld in dát schema (`notitie` bij
 * acquisitie / per-klant-CRM, `details` bij de globale CRM-route), zodat de
 * veldfout inline op het juiste veld landt.
 */
export function controleerVervolgInhoud(
  waarde: { vervolgOpId?: string | null; [veld: string]: unknown },
  inhoudVeld: string,
  ctx: z.RefinementCtx,
): void {
  if (!waarde.vervolgOpId) return
  const inhoud = waarde[inhoudVeld]
  const tekst = typeof inhoud === 'string' ? inhoud.trim() : ''
  if (tekst.length < CONTACTMOMENT_VERVOLG_INHOUD_MIN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [inhoudVeld],
      message: VERVOLG_INHOUD_TE_KORT,
    })
  }
}

/**
 * Invoer van het gedeelde inline "+ Vervolg"-formulier, in domein-neutrale
 * veldnamen. De aanroeper (server-action per domein) vertaalt dit naar het
 * eigen API-body-formaat.
 */
export const vervolgInvoerSchema = z.object({
  vervolgOpId: z.string().min(1),
  /** ISO datetime - datum/tijd van het vervolg (default nu, aanpasbaar). */
  datum: z.string().min(1),
  /** Domein-enum; de route valideert de exacte waarde. */
  type: z.string().min(1),
  onderwerp: z.string().trim().min(1).max(200),
  inhoud: z.string().trim().min(CONTACTMOMENT_VERVOLG_INHOUD_MIN, VERVOLG_INHOUD_TE_KORT).max(10000),
  /** Nieuwe (optionele) vervolgcontact-datum, ISO of null. */
  vervolgDatum: z.string().min(1).nullable().optional(),
})
export type VervolgInvoer = z.infer<typeof vervolgInvoerSchema>

/** Logische veldnamen van het inline formulier (waar fouten inline landen). */
export const VERVOLG_VELDEN = ['datum', 'type', 'onderwerp', 'inhoud', 'vervolgDatum'] as const
export type VervolgVeld = (typeof VERVOLG_VELDEN)[number]

/**
 * De logische veldnamen mappen op zichzelf, zodat een fout die al in
 * formulier-termen binnenkomt (client-side voorvalidatie met
 * `vervolgInvoerSchema`) net zo goed inline landt als een API-veldfout.
 */
const LOGISCHE_VELDMAP: Record<string, VervolgVeld> = {
  datum: 'datum',
  type: 'type',
  onderwerp: 'onderwerp',
  inhoud: 'inhoud',
  vervolgDatum: 'vervolgDatum',
}

/** API-veld → logisch veld, acquisitie-route (`/acquisitie/.../contactmomenten`). */
export const ACQUISITIE_VERVOLG_VELDMAP: Record<string, VervolgVeld> = {
  ...LOGISCHE_VELDMAP,
  notitie: 'inhoud',
  opvolgingDatum: 'vervolgDatum',
}

/** API-veld → logisch veld, CRM-routes (globaal `/contact-moments` + per klant). */
export const CRM_VERVOLG_VELDMAP: Record<string, VervolgVeld> = {
  ...LOGISCHE_VELDMAP,
  date: 'datum',
  summary: 'onderwerp',
  details: 'inhoud',
  notitie: 'inhoud',
  opvolgingDate: 'vervolgDatum',
  opvolgingDatum: 'vervolgDatum',
}

/**
 * Zet een API-validatie-envelope om naar per-veld fouten in de logische
 * veldnamen van het inline formulier, zodat de gedeelde component ze inline kan
 * tonen (nooit een generieke "Validatiefout"-toast, zie CLAUDE.md).
 */
export function vervolgVeldFouten(
  err: ApiValidationError | null | undefined,
  veldmap: Record<string, VervolgVeld>,
): Partial<Record<VervolgVeld, string>> {
  const uit: Partial<Record<VervolgVeld, string>> = {}
  for (const [veld, melding] of Object.entries(issuesToFieldErrors(err))) {
    const logisch = veldmap[veld]
    if (logisch && !uit[logisch]) uit[logisch] = melding
  }
  return uit
}

/**
 * Vertaalt een mislukte opslag naar wat de inline-invoer moet tonen: per-veld
 * fouten waar dat kan, en anders de CONCRETE melding uit `issues` in plaats van
 * het generieke "Ongeldige invoer" (bv. een fout op `vervolgOpId`, waarvoor het
 * formulier geen eigen veld heeft).
 */
export function vervolgFout(
  resultaat: { error: string; issues?: ApiValidationIssue[] },
  veldmap: Record<string, VervolgVeld>,
): { error: string; veldFouten: Partial<Record<VervolgVeld, string>> } {
  const veldFouten = vervolgVeldFouten({ issues: resultaat.issues }, veldmap)
  if (Object.keys(veldFouten).length > 0) return { error: resultaat.error, veldFouten }
  const eerste = resultaat.issues?.[0]?.message
  return { error: eerste ?? resultaat.error, veldFouten }
}

/**
 * Client-side voorcontrole van de datum tegen het bovenliggende moment; de
 * server doet dezelfde check autoritatief (zelfde tekst). Geeft `null` als het
 * in orde is.
 */
export function vervolgDatumFout(
  datum: string | Date,
  bovenliggendeDatum: string | Date,
): string | null {
  const nieuw = datum instanceof Date ? datum : new Date(datum)
  const boven = bovenliggendeDatum instanceof Date ? bovenliggendeDatum : new Date(bovenliggendeDatum)
  if (Number.isNaN(nieuw.getTime())) return 'Ongeldige datum'
  if (Number.isNaN(boven.getTime())) return null
  return nieuw.getTime() < boven.getTime() ? VERVOLG_DATUM_VOOR_BOVENLIGGEND : null
}
